export type AuthFlowOptions = {
  invitationId?: string;
  redirectPath?: string;
  invitedEmail?: string;
  invitedOrganizationName?: string;
  invitedOrganizationId?: string;
  signupEmail?: string;
  signupOrganizationName?: string;
};

export type PendingInvitationContext = {
  invitationId: string;
  invitedEmail?: string;
  invitedOrganizationName?: string;
  invitedOrganizationId?: string;
  redirectPath?: string;
};

export type InvitationRecoveryAction = "redirect" | "invalid" | "switch-account" | "stay";

export type InvitationRecoveryState = {
  action: InvitationRecoveryAction;
  error: string;
};

export const INVITATION_RECIPIENT_MISMATCH =
  "You are not the recipient of the invitation";
export const INVITATION_NOT_FOUND = "Invitation not found";
export const FAILED_TO_RETRIEVE_INVITATION = "Failed to retrieve invitation";
const PENDING_INVITATION_STORAGE_KEY = "pending-workspace-invitation";

export const buildAuthCallbackUrl = (
  origin: string,
  {
    invitationId,
    redirectPath,
    invitedEmail,
    invitedOrganizationName,
    invitedOrganizationId,
    signupEmail,
    signupOrganizationName,
  }: AuthFlowOptions = {},
): string => {
  const url = new URL(invitationId ? "/invite" : "/login", origin);
  url.searchParams.set("verified", "1");

  if (invitationId) {
    url.searchParams.set("invitation", invitationId);
  }

  if (invitedEmail) {
    url.searchParams.set("email", invitedEmail.trim().toLowerCase());
  }

  if (invitedOrganizationName) {
    url.searchParams.set("org", invitedOrganizationName.trim());
  }

  if (invitedOrganizationId) {
    url.searchParams.set("orgId", invitedOrganizationId);
  }

  if (redirectPath) {
    url.searchParams.set("redirect", redirectPath);
  }

  if (!invitationId && signupEmail) {
    url.searchParams.set("signup", "1");
    url.searchParams.set("signupEmail", signupEmail.trim().toLowerCase());
  }

  if (!invitationId && signupOrganizationName) {
    url.searchParams.set("pendingOrg", signupOrganizationName.trim());
  }

  return url.toString();
};

export const resolveInvitationRecovery = (message: string): InvitationRecoveryState => {
  if (
    message.includes(INVITATION_NOT_FOUND) ||
    message.includes(FAILED_TO_RETRIEVE_INVITATION)
  ) {
    return {
      action: "invalid",
      error: "This invitation is no longer valid. Ask the workspace admin to send you a new invite.",
    };
  }

  if (message.includes(INVITATION_RECIPIENT_MISMATCH)) {
    return {
      action: "switch-account",
      error: "This invitation belongs to a different email address. Sign out and use the invited account.",
    };
  }

  return {
    action: "stay",
    error: message || "Unable to accept invitation.",
  };
};

export const shouldFallbackToInviteSignin = (message: string): boolean => {
  const normalized = message.trim().toLowerCase();

  return (
    normalized.includes("already exists") ||
    normalized.includes("user already exists") ||
    normalized.includes("email already exists") ||
    normalized.includes("account already exists")
  );
};

export const buildInvitePath = ({
  invitationId,
  invitedEmail,
  invitedOrganizationName,
  invitedOrganizationId,
  redirectPath,
}: PendingInvitationContext): string => {
  const searchParams = new URLSearchParams();
  searchParams.set("invitation", invitationId);

  if (invitedEmail) {
    searchParams.set("email", invitedEmail.trim().toLowerCase());
  }

  if (invitedOrganizationName) {
    searchParams.set("org", invitedOrganizationName.trim());
  }

  if (invitedOrganizationId) {
    searchParams.set("orgId", invitedOrganizationId);
  }

  if (redirectPath) {
    searchParams.set("redirect", redirectPath);
  }

  return `/invite?${searchParams.toString()}`;
};

export const buildInviteLoginPath = ({
  invitationId,
  invitedEmail,
  invitedOrganizationName,
  invitedOrganizationId,
  redirectPath,
}: PendingInvitationContext): string => {
  const searchParams = new URLSearchParams();
  searchParams.set(
    "redirect",
    buildInvitePath({
      invitationId,
      invitedEmail,
      invitedOrganizationName,
      invitedOrganizationId,
      redirectPath,
    }),
  );

  if (invitedEmail) {
    searchParams.set("email", invitedEmail.trim().toLowerCase());
  }

  searchParams.set("invite", "1");
  return `/login?${searchParams.toString()}`;
};

export const buildForgotPasswordPath = ({
  redirectPath,
  email,
}: {
  redirectPath?: string;
  email?: string;
} = {}): string => {
  const searchParams = new URLSearchParams();

  if (redirectPath) {
    searchParams.set("redirect", redirectPath);
  }

  if (email) {
    searchParams.set("email", email.trim().toLowerCase());
  }

  const query = searchParams.toString();
  return query ? `/forgot-password?${query}` : "/forgot-password";
};

export const buildResetPasswordRedirectUrl = ({
  origin,
  redirectPath,
  email,
}: {
  origin: string;
  redirectPath?: string;
  email?: string;
}): string => {
  const url = new URL("/reset-password", origin);

  if (redirectPath) {
    url.searchParams.set("redirect", redirectPath);
  }

  if (email) {
    url.searchParams.set("email", email.trim().toLowerCase());
  }

  return url.toString();
};

export const appendLoginStatus = (
  path: string,
  status: "reset" | "verified",
): string => {
  const url = new URL(path, "http://localhost");
  url.searchParams.set(status, "1");
  return `${url.pathname}${url.search}`;
};

export const storePendingInvitation = (context: PendingInvitationContext): void => {
  window.localStorage.setItem(
    PENDING_INVITATION_STORAGE_KEY,
    JSON.stringify({
      invitationId: context.invitationId,
      invitedEmail: context.invitedEmail?.trim().toLowerCase() || undefined,
      invitedOrganizationName: context.invitedOrganizationName?.trim() || undefined,
      invitedOrganizationId: context.invitedOrganizationId || undefined,
      redirectPath: context.redirectPath || undefined,
    }),
  );
};

export const readPendingInvitation = (): PendingInvitationContext | null => {
  const raw = window.localStorage.getItem(PENDING_INVITATION_STORAGE_KEY);
  if (!raw) {
    return null;
  }

  try {
    const parsed = JSON.parse(raw) as Partial<PendingInvitationContext>;
    if (!parsed.invitationId || typeof parsed.invitationId !== "string") {
      throw new Error("Missing invitation id");
    }

    return {
      invitationId: parsed.invitationId,
      invitedEmail:
        typeof parsed.invitedEmail === "string" && parsed.invitedEmail
          ? parsed.invitedEmail.trim().toLowerCase()
          : undefined,
      invitedOrganizationName:
        typeof parsed.invitedOrganizationName === "string" && parsed.invitedOrganizationName
          ? parsed.invitedOrganizationName.trim()
          : undefined,
      invitedOrganizationId:
        typeof parsed.invitedOrganizationId === "string" && parsed.invitedOrganizationId
          ? parsed.invitedOrganizationId
          : undefined,
      redirectPath:
        typeof parsed.redirectPath === "string" && parsed.redirectPath
          ? parsed.redirectPath
          : undefined,
    };
  } catch {
    window.localStorage.removeItem(PENDING_INVITATION_STORAGE_KEY);
    return null;
  }
};

export const clearPendingInvitation = (): void => {
  window.localStorage.removeItem(PENDING_INVITATION_STORAGE_KEY);
};
